import { gql, useQuery } from '@apollo/client';
import { gqlTypes } from '../../types/gqlTypes';
import { ShippingAddress } from './address';

/**
 * Composed from generated *schema* types rather than a generated *operation* type, for the same
 * reason as the purchase mutation: `yarn gql:types` needs the web API on localhost:8080.
 */
type BillingPrefillQueryResult = {
  currentUser:
    | (Pick<gqlTypes.User, 'email'> & {
        billingAccount:
          | (Pick<gqlTypes.BillingAccount, 'firstName' | 'lastName' | 'prefillAddress'> & {
              billingInfo: {
                paymentInfo:
                  | gqlTypes.ObfuscatedCardInfo
                  | { __typename: 'PayPalInfo' | 'BankAccountInfo' | 'WalletPaymentInfo' };
              } | null;
            })
          | null;
      })
    | null;
};

const billingPrefillQuery = gql`
  query NANO_billingPrefill {
    currentUser {
      id
      email
      billingAccount {
        firstName
        lastName
        prefillAddress {
          line1
          line2
          city
          state
          zip
          country
          phone
        }
        billingInfo {
          paymentInfo {
            __typename
            ... on ObfuscatedCardInfo {
              lastFour
              type
              month
              year
            }
          }
        }
      }
    }
  }
`;

export interface CardOnFile {
  brand: string;
  lastFour: number;
  month: number;
  year: number;
}

export interface BillingPrefill {
  loading: boolean;
  /** the shopper's Fi account email, which always exists because checkout requires a session */
  accountEmail: string | null;
  /** the address Recurly holds, server-resolved (account, then payment method, then last invoice) */
  billingAddress: ShippingAddress | null;
  cardOnFile: CardOnFile | null;
}

function cardOnFileFrom(paymentInfo: BillingPrefillQueryResult['currentUser']): CardOnFile | null {
  const info = paymentInfo?.billingAccount?.billingInfo?.paymentInfo;
  if (!info || info.__typename !== 'ObfuscatedCardInfo') {
    return null;
  }
  const now = new Date();
  if (info.year * 100 + info.month < now.getFullYear() * 100 + now.getMonth() + 1) {
    return null;
  }
  return { brand: info.type, lastFour: info.lastFour, month: info.month, year: info.year };
}

function billingAddressFrom(user: BillingPrefillQueryResult['currentUser']): ShippingAddress | null {
  const account = user?.billingAccount;
  if (!account?.prefillAddress) {
    return null;
  }
  return {
    firstName: account.firstName ?? '',
    lastName: account.lastName ?? '',
    line1: account.prefillAddress.line1,
    line2: account.prefillAddress.line2 ?? '',
    city: account.prefillAddress.city,
    state: account.prefillAddress.state,
    zip: account.prefillAddress.zip,
    country: account.prefillAddress.country || 'US',
    phone: account.prefillAddress.phone ?? '',
  };
}

/**
 * What the shopper's Fi account can prefill checkout with: their account email, the address Recurly
 * holds, and the card their membership already bills to.
 *
 * The shopper is always authenticated here -- registration required logging in or creating an account
 * -- so the email always resolves. The address and card exist only for shoppers who have bought
 * before; registration's contact info takes precedence over both when it captured an address, since
 * it is fresher.
 *
 * Only a plain card is offered as a card on file: PayPal, bank accounts and wallet-stored cards need
 * their own confirmation flows. An expired card is treated as absent, since offering it just moves
 * the decline to after the confirm button.
 *
 * A query error means "nothing to prefill": this is an optimisation, and a shopper whose account
 * could not be read can still type everything.
 */
export function useBillingPrefill(): BillingPrefill {
  const { data, loading } = useQuery<BillingPrefillQueryResult>(billingPrefillQuery, {
    fetchPolicy: 'network-only',
  });

  if (loading || !data?.currentUser) {
    return { loading, accountEmail: null, billingAddress: null, cardOnFile: null };
  }

  return {
    loading,
    accountEmail: data.currentUser.email,
    billingAddress: billingAddressFrom(data.currentUser),
    cardOnFile: cardOnFileFrom(data.currentUser),
  };
}
